/** AiOpsDashboardView 순수 헬퍼 — API 응답 → UI 뷰모델 매핑, addition 빌더. */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { utcZeroTimeToStandardTime } from '@/common/utils/browser/conversion';
import type { AlertTargetItem } from '@/alert/components/alertTargetList/alertTargetList.types';
import { CHECK_BY_COMBINATION, CHECK_BY_TARGET } from '@/alert/utils/define';
import type { RuleCriteria } from '@/alert/components/alertDetail/alertDetail.types';
import type { EventAlertDetailInfo } from '@/alert/utils/types';
import type { Tag } from '@/api-client/alert/model';
import type {
  AlertRuleSummaryItem,
  AnomalyCount,
  BusinessGroupItem,
  BusinessItem,
  InsightSummaryItem,
  TimelinePoint,
  UserAlertCount,
} from '@/api-client/aiops/model';
import type { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import { scoreToSeverity } from '@/aiops/utils/scoring.utils';
import { EMPTY_VALUE_TEXT } from '@/aiops/utils/severity.define';
import type {
  BusinessGroupView,
  BusinessView,
  TargetView,
  TimelinePointView,
} from '@/aiops/utils/dashboard.types';
import type { AlertSeverity } from '@/aiops/components/AlertInfoPanel/alertInfoPanel.types';
import type { DashboardAlertItem } from './aiOpsDashboardView.types';

dayjs.extend(utc);

/* BusinessItem.summary 의 dashboard 형태 — API 스펙 정리 전까지 로컬 타입.
   hsLevel/minHsLevel 은 매핑하지 않는다: severity 는 scoreToSeverity() 단일 진입점으로만 도출(임계치 소스 2개 방지). */
interface DashboardBusinessSummary {
  healthScore?: number;
  /** 해당 비즈니스 하위 타겟까지 반영한 최저 score — 서버 계산값. 클라이언트에서 재계산 불가. */
  minHealthScore?: number;
  concurrentUser?: number;
  tps?: number;
  anomaly?: AnomalyCount;
  userAlert?: UserAlertCount;
}

/** 카드 영역 표기 상태 — SPEC.md '골격으로 카드를 그리지 않는다' 참조. */
export type DataViewState = 'loading' | 'error' | 'empty' | 'ready';

/**
 * 골격(business-groups) + 메트릭(business-data) 쿼리 상태 → 카드 영역 표기 상태.
 * 골격을 먼저 판정한다 — business-data 는 골격이 있을 때만 enabled 이라, 골격 실패·0 건이면
 * 실행되지 않아 isPending 이 영구 true 다(그대로 흘리면 영구 'loading').
 */
export const resolveDataViewState = ({
  isSkeletonPending,
  isSkeletonError,
  hasSkeleton,
  isDataPending,
  isDataError,
  groupCount,
}: {
  isSkeletonPending: boolean;
  isSkeletonError: boolean;
  hasSkeleton: boolean;
  isDataPending: boolean;
  isDataError: boolean;
  groupCount: number;
}): DataViewState => {
  if (isSkeletonPending) return 'loading';
  if (isSkeletonError) return 'error';
  /* 골격 0 건 — 그릴 그룹 자체가 없다(business-data 는 enabled=false 로 영구 pending). */
  if (!hasSkeleton) return 'empty';
  if (isDataPending) return 'loading';
  if (isDataError) return 'error';
  return groupCount > 0 ? 'ready' : 'empty';
};

/**
 * 5 개 조회의 최초 완료 대기 여부 — 헤더·타임라인·카드·패널이 한 번에 드러나도록 모두 기다린다
 * (business-data 만 보면 나머지 3 개가 뒤늦게 채워져 순차 깜빡임이 된다).
 *
 * 조회 사슬: 골격 → business-data → (alerts·insights·timeline 병렬). 하위 3 개는 `isFetched` 로
 * 판정한다 — enabled=false 인 조회는 시작하지 않아 `isPending` 으로는 "시작 안 함"과 "진행 중"을
 * 구분할 수 없다. 실행되지 않는 조회는 대기 대상에서 뺀다.
 */
export const resolveIsInitialLoading = ({
  isSkeletonFetched,
  hasSkeleton,
  hasLoadedData,
  hasFilteredGroups,
  isAlertsFetched,
  isInsightsFetched,
  isTimelineFetched,
}: {
  isSkeletonFetched: boolean;
  /**
   * 골격 응답의 그룹이 1 건 이상인지. **`isSkeletonFetched` 와 같은 쿼리 결과에서 읽어야 한다** —
   * fetch 완료 플래그가 먼저 서고 데이터가 늦게 반영되면 그 프레임에 '골격 0 건'으로 오판해
   * 마스크가 조기에 걷힌다. vue-query 는 `data`/`isFetched` 를 같은 result 객체로 내보내므로
   * 두 값을 같은 훅 반환에서 꺼내면 이 순서가 갈리지 않는다.
   */
  hasSkeleton: boolean;
  /**
   * business-data 의 **최초 응답을 받았는지**(latch — 재조회로 되돌아가지 않는다).
   * 여기만 `isFetched` 가 아니라 latch 를 쓰는 이유: business-data 는 헤더 필터와 무관하게 항상
   * 전체 그룹으로 5 초마다 폴링된다(필터 pill 의 Health Score 모수). `isFetched` 로 판정하면 매
   * 폴링마다 대기 상태로 되돌아가, 필터 대상이 0 건이라 아래 `hasFilteredGroups` 로 빠져나가야 할
   * 상황에서도 그 전에 `true` 를 반환한다 → **사용자 조작과 무관한 tick 에 마스크가 뜬다**(실제 회귀).
   */
  hasLoadedData: boolean;
  /** 하위 3 개 조회의 `enabled` 조건과 **같은 값**. 갈리면 실행되지 않는 조회를 기다려 마스크가 남는다. */
  hasFilteredGroups: boolean;
  isAlertsFetched: boolean;
  isInsightsFetched: boolean;
  isTimelineFetched: boolean;
}): boolean => {
  if (!isSkeletonFetched) return true;
  /* 골격 0 건 — 하위 조회가 모두 enabled=false 라 더 기다릴 것이 없다. */
  if (!hasSkeleton) return false;
  if (!hasLoadedData) return true;
  /* 대상 그룹 0 — 하위 3 개가 enabled=false. 빈 화면으로 그린다. */
  if (!hasFilteredGroups) return false;
  return !isAlertsFetched || !isInsightsFetched || !isTimelineFetched;
};

/**
 * 멈춤 상태에서 표시할 조회 시점(epoch ms). 재생 중이면 `null`(호출부가 최신 시각으로 표시).
 *
 * 사용자가 고른 시각은 응답과 무관한 사용자 상태다 — 응답(`lastPointTime`) 유무로 판정하면
 * 헤더 필터를 모두 해제해 timeline 조회가 멈춘 사이 조회 시점이 현재로 튀었다가 되돌아온다.
 * 명시적 선택이 없을 때만 응답의 마지막 tick 으로 폴백한다.
 */
export const resolveSelectedTickTime = ({
  isPaused,
  selectedTime,
  lastPointTime,
}: {
  isPaused: boolean;
  /** 사용자가 명시적으로 고른 tick. 없으면 `null`. */
  selectedTime: number | null;
  /** 응답의 마지막 tick. 없으면 `null`. */
  lastPointTime: number | null;
}): number | null => {
  if (!isPaused) return null;
  return selectedTime ?? lastPointTime;
};

/** 그룹명에서 표시용 짧은 이름 추출 — "/" 포함 시 마지막 segment(예: "업무계/대출/소매대출" → "소매대출"), 없으면 원본. */
export const toGroupDisplayName = (name: string): string => {
  const idx = name.lastIndexOf('/');
  return idx === -1 ? name : name.slice(idx + 1);
};

/* ===== API 응답 → UI 뷰모델 변환 =====
   메트릭 누락은 모두 null(미수신) — 0 폴백은 실측 0 과 구분되지 않아 허위 Critical·허위 '이상 0건' 을 만든다.
   severity 는 항상 scoreToSeverity(score) 로 도출한다(서버 hsStatus 무시). */

/** 데이터 미수신 — 화면에서 '-' 로 표기된다. */
const NO_DATA = null;

const PLACEHOLDER_TARGET: TargetView = {
  id: '',
  name: '',
  type: 'Host',
  concurrentUsers: NO_DATA,
  tps: NO_DATA,
  anomaly: { critical: NO_DATA, warning: NO_DATA, unknown: NO_DATA },
  alarm: { critical: NO_DATA, warning: NO_DATA },
};

/** Summary 를 BusinessView 메트릭 필드에만 적용(골격 필드 보존). business-groups/business-data 공용. */
const applyBusinessSummary = (
  base: BusinessView,
  summary: DashboardBusinessSummary | undefined,
): BusinessView => {
  if (!summary) return base;
  const score = summary.healthScore ?? NO_DATA;
  return {
    ...base,
    score,
    /* 서버 minHealthScore 를 그대로 보존 — 하위 타겟 최저값이므로 비즈니스 score 로는 재계산할 수 없다. */
    minScore: summary.minHealthScore ?? NO_DATA,
    severity: scoreToSeverity(score),
    concurrentUsers: summary.concurrentUser ?? NO_DATA,
    tps: summary.tps ?? NO_DATA,
    anomaly: {
      critical: summary.anomaly?.critical ?? NO_DATA,
      warning: summary.anomaly?.warning ?? NO_DATA,
      unknown: summary.anomaly?.unknown ?? NO_DATA,
    },
    alarm: {
      critical: summary.userAlert?.critical ?? NO_DATA,
      warning: summary.userAlert?.warning ?? NO_DATA,
    },
  };
};

/**
 * 전체 타겟 개수 = active + unknown. 둘 다 누락일 때만 null(미수신) —
 * 한쪽만 누락이면 그쪽을 0 으로 보고 합산한다(합계 자체는 수신된 값이므로 '-' 로 감추지 않는다).
 */
const toTotalTargetCount = (item: BusinessItem): number | null => {
  const { activeTargetCount, unknownTargetCount } = item;
  if (activeTargetCount == null && unknownTargetCount == null) return NO_DATA;
  return (activeTargetCount ?? 0) + (unknownTargetCount ?? 0);
};

const transformBusinessItem = (item: BusinessItem): BusinessView => {
  /* summary 미수신 placeholder — 0/'critical' 도 'normal' 도 쓰지 않는다(허위 Critical / 미수신을 '정상' 으로 오표기). */
  const placeholder: BusinessView = {
    id: item.id ?? '',
    /* 이름도 응답이 유일한 출처 — 누락은 '-'(다른 메트릭과 동일 표기). */
    name: item.name ?? EMPTY_VALUE_TEXT,
    score: NO_DATA,
    minScore: NO_DATA,
    severity: 'unknown',
    concurrentUsers: NO_DATA,
    tps: NO_DATA,
    anomaly: { critical: NO_DATA, warning: NO_DATA, unknown: NO_DATA },
    alarm: { critical: NO_DATA, warning: NO_DATA },
    targets: buildPlaceholderTargets(toTotalTargetCount(item) ?? 0),
    /* 표기용 개수는 원본 유지 — targets[] 길이로는 미수신과 실측 0개를 구분할 수 없다. */
    targetCount: toTotalTargetCount(item),
    activeTargetCount: item.activeTargetCount ?? NO_DATA,
    unknownTargetCount: item.unknownTargetCount ?? NO_DATA,
  };
  return applyBusinessSummary(placeholder, item.summary as DashboardBusinessSummary | undefined);
};

/** targetCount 만큼 placeholder TargetView 배열 생성 — 카드의 "N개" 표시용. */
const buildPlaceholderTargets = (count: number): TargetView[] =>
  Array.from({ length: count }, () => PLACEHOLDER_TARGET);

export const transformBusinessGroupItems = (
  items: readonly BusinessGroupItem[],
): BusinessGroupView[] =>
  items.map((item) => ({
    id: item.id ?? '',
    name: item.name ?? '',
    businesses: (item.businesses ?? []).map(transformBusinessItem),
  }));

/**
 * business-data 응답을 골격(base) 트리에 머지. 상세 근거는 SPEC.md '해당 tick 에 존재하지 않는 비즈니스'.
 *
 * - 비즈니스 모수·필드 = 응답. 골격에 없는 비즈니스도 그린다(골격은 collectTime 없는 '현재' 목록이라 과거 tick 판정 근거가 못 된다).
 * - 그룹 모수 = 골격. 응답에 없는 그룹은 제외하고, 그룹 name 은 골격만 갖고 있다.
 * - 응답이 비면 빈 배열을 반환한다 — 골격으로 폴백하지 않는다. 로딩·실패는 카드가 아니라
 *   호출부의 isPending/isError 상태 UI 가 표현한다(골격 카드는 메트릭이 전부 '-' 인 유령 카드).
 */
export const mergeBusinessData = (
  baseGroups: readonly BusinessGroupView[],
  dataGroups: readonly BusinessGroupItem[],
): BusinessGroupView[] => {
  const dataGroupMap = new Map<string, BusinessGroupItem>();
  for (const g of dataGroups) {
    if (g.id) dataGroupMap.set(g.id, g);
  }

  return baseGroups.reduce<BusinessGroupView[]>((groupAcc, baseGroup) => {
    const dataGroup = dataGroupMap.get(baseGroup.id);
    if (!dataGroup) return groupAcc;

    groupAcc.push({
      ...baseGroup,
      businesses: (dataGroup.businesses ?? []).map(transformBusinessItem),
    });
    return groupAcc;
  }, []);
};

/* ===== UA(사용자 알람) — /aiops/dashboard/alerts 매핑 =====
   AlertRuleSummaryItem 은 알람 룰 단위(group/business 컨텍스트 없음). */

const ALERT_LEVEL_TO_SEVERITY: Record<string, AlertSeverity> = {
  Critical: 'critical',
  CRITICAL: 'critical',
  critical: 'critical',
  Warning: 'warning',
  WARNING: 'warning',
  warning: 'warning',
};

const toAlertSeverity = (level: string | undefined): AlertSeverity =>
  ALERT_LEVEL_TO_SEVERITY[level ?? ''] ?? 'unknown';

/** triggerTime(UTC 문자열) → myInfo.timezone 기준 표시 텍스트. */
const formatTriggerTime = (iso: string | undefined): string =>
  iso ? utcZeroTimeToStandardTime(iso) : '';

/** id 해석 — 카드 표시와 슬라이드 addition lookup 이 같은 키를 쓰도록 한 곳에서 결정. */
export const resolveAlertRuleId = (item: AlertRuleSummaryItem, index: number): string =>
  item.resultId ?? item.alertRuleId ?? `alert-${index}`;

/** 알 수 없는 값은 byTarget 으로 폴백. */
const toRuleCriteria = (raw: string | undefined): RuleCriteria =>
  raw === CHECK_BY_COMBINATION ? CHECK_BY_COMBINATION : CHECK_BY_TARGET;

/** triggerTime 기준 -10분 윈도우 — EventAlertDetail History 탭 차트 시간 범위용. */
const buildTimePeriodFromTrigger = (iso: string | undefined): TimePeriodInfo => {
  /* triggerTime 은 UTC 0 문자열 — dayjs.utc 로 파싱해야 fromTimeUtc/toTimeUtc 가 tz offset 만큼 어긋나지 않음. */
  const to = iso ? dayjs.utc(iso) : dayjs.utc();
  const from = to.subtract(10, 'minute');
  return {
    timePeriod: 'p10m',
    fromTime: from.format('YYYY-MM-DD HH:mm:ss'),
    toTime: to.format('YYYY-MM-DD HH:mm:ss'),
    fromTimeUtc: from.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
    toTimeUtc: to.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
    isPaused: false,
  };
};

export const mapAlertRuleItem = (item: AlertRuleSummaryItem, index: number): DashboardAlertItem => {
  const targets: AlertTargetItem[] = (item.targets ?? []).map((t) => ({
    id: t.targetId,
    name: t.targetName ?? '',
  }));
  return {
    id: resolveAlertRuleId(item, index),
    severity: toAlertSeverity(item.alertLevel),
    title: item.alertRuleName ?? '',
    time: formatTriggerTime(item.triggerTime),
    /* group/business 컨텍스트는 응답에 없음 — subName 을 카테고리 자리에 표시. */
    category: item.subName ?? '',
    target: targets[0]?.name ?? '',
    targets,
    tag: item.eventType ?? item.alertRuleType ?? '',
  };
};

export const buildEventAlertAdditionFromAlertRule = (
  item: AlertRuleSummaryItem,
): EventAlertDetailInfo => {
  const tags: Tag[] = (item.targets ?? []).map((t) => ({
    tagId: t.targetId,
    tagName: t.targetName,
  }));
  return {
    resultId: item.resultId ?? '',
    ruleId: item.alertRuleId ?? '',
    ruleType: toRuleCriteria(item.ruleCriteria),
    timePeriodInfo: buildTimePeriodFromTrigger(item.triggerTime),
    info: [
      {
        resultId: item.resultId,
        ruleId: item.alertRuleId,
        ruleName: item.alertRuleName,
        level: item.alertLevel,
        eventType: item.eventType,
        type: item.alertRuleType,
        tags,
        /* Detail Information 탭 메트릭 — 응답에 stats 없음. 추후 별도 API 로 채울 자리. */
        stats: [],
      },
    ],
  };
};

/* ===== TL(Timeline) — /aiops/dashboard/timeline 매핑 ===== */
export const mapTimelinePoint = (point: TimelinePoint): TimelinePointView => ({
  time: point.triggerTime ? dayjs.utc(point.triggerTime).valueOf() : 0,
  anomaly: point.anomalyCount ?? 0,
  alarm: point.userAlertCount ?? 0,
});

/* ===== AI(인사이트 리스트) — /aiops/insights 매핑 ===== */

const INSIGHT_SEVERITY_TO_TONE: Record<string, AlertSeverity> = {
  CRITICAL: 'critical',
  URGENT: 'critical',
  WARNING: 'warning',
  UNKNOWN: 'unknown',
};

/** AI 인사이트 카드 id 해석 — InsightDetail addition.rcaId 와 동일한 키로 사용. */
export const resolveInsightId = (item: InsightSummaryItem, index: number): string =>
  item.rcaId ?? `insight-${index}`;

export const mapInsightItem = (item: InsightSummaryItem, index: number): DashboardAlertItem => {
  const targetName = item.targetName ?? '';
  const groupNames = (item.businessGroups ?? [])
    .map((g) => g.businessGroupName)
    .filter(Boolean)
    .join(', ');
  return {
    id: resolveInsightId(item, index),
    severity: INSIGHT_SEVERITY_TO_TONE[item.severity ?? ''] ?? 'unknown',
    title: item.rcaName ?? item.labelName ?? '',
    time: formatTriggerTime(item.triggerTime),
    category: groupNames,
    target: targetName,
    /* AlertTargetList 호환 — 단일 타겟을 1개짜리 배열로 노출. */
    targets: targetName ? [{ id: item.targetId, name: targetName }] : [],
    tag: item.labelName ?? item.platform ?? '',
  };
};

export const buildInsightAdditionFromInsight = (
  item: InsightSummaryItem,
): {
  ruleName: string;
  rcaId: string;
  collectTime: string;
  labelName: string;
} => ({
  ruleName: item.rcaName ?? '',
  rcaId: item.rcaId ?? '',
  labelName: item.labelName ?? '',
  collectTime: item.collectTime ?? '',
});
